import React, { useEffect, useRef, useState } from "react";
import "./CubeSliderMain.css";
import { SendPostRequest } from "../../http";
import { useSelector } from "react-redux";

const CubeSliderMain = () => {
  const containerRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(1); // Start at first real slide
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [depth, setDepth] = useState(450);
  const [homeBanners, setHomeBanners] = useState([]);
  const [displayBanners, setDisplayBanners] = useState([]);
  const sitedata = useSelector((store) => store.global.loginData);
  const [animatedSlide, setAnimatedSlide] = useState(null);
  const TRANSITION_MS = 2000; // Slower transition
  const AUTO_SLIDE_DELAY = 6000; // 6 seconds total (3s animation + 5s delay)

  // Fetch banners
  useEffect(() => {
    const brandingData = async () => {
      const siteTheme = sitedata?.options?.theme;
      const response = await SendPostRequest("branding/banners", { theme: siteTheme });
      setHomeBanners(response?.data || []);
    };
    brandingData();
  }, [sitedata]);

  // Prepare display banners with clones for infinite loop
  useEffect(() => {
    if (homeBanners.length > 0) {
      const firstClone = { ...homeBanners[0], id: 'clone-first' };
      const lastClone = { ...homeBanners[homeBanners.length - 1], id: 'clone-last' };

      setDisplayBanners([lastClone, ...homeBanners, firstClone]);
      setAnimatedSlide(1); // Set first real slide as animated
    }
  }, [homeBanners]);

  // Compute depth dynamically
  useEffect(() => {
    const computeDepth = () => {
      const container = containerRef.current;
      if (!container) return;

      const h = container.clientHeight || window.innerHeight;
      setDepth(Math.max(300, Math.round(h * 0.6)));
    };

    computeDepth();
    window.addEventListener("resize", computeDepth);
    return () => window.removeEventListener("resize", computeDepth);
  }, []);

  const realFaceCount = homeBanners.length;
  const faceCount = displayBanners.length;
  const angleStep = realFaceCount > 0 ? 360 / realFaceCount : 0;
  const activeIndex = ((currentSlide - 1) % realFaceCount + realFaceCount) % realFaceCount;

  const navigateSlide = (direction) => {
    if (isTransitioning || realFaceCount === 0) return;

    setIsTransitioning(true);

    setCurrentSlide((prevSlide) => {
      let newSlide = prevSlide + direction;

      // Handle clone transitions seamlessly
      if (newSlide >= faceCount - 1) {
        // Going to last clone, then jump to first real slide
        setTimeout(() => {
          setCurrentSlide(1);
          setAnimatedSlide(1);
          setIsTransitioning(false);
        }, TRANSITION_MS);
      } else if (newSlide <= 0) {
        // Going to first clone, then jump to last real slide
        setTimeout(() => {
          setCurrentSlide(faceCount - 2);
          setAnimatedSlide(faceCount - 2);
          setIsTransitioning(false);
        }, TRANSITION_MS);
      } else {
        // Normal slide change
        setTimeout(() => {
          setIsTransitioning(false);
        }, TRANSITION_MS);
      }

      // Set the new slide to be animated only if it's a real slide
      if (newSlide > 0 && newSlide < faceCount - 1) {
        setAnimatedSlide(newSlide);
      }

      return newSlide;
    });
  };

  const nextSlide = () => navigateSlide(1);
  const prevSlide = () => navigateSlide(-1);

  const goToSlide = (index) => {
    if (isTransitioning || index === activeIndex || realFaceCount === 0) return;

    setIsTransitioning(true);
    const targetSlide = index + 1; // +1 because of the clone at beginning

    setCurrentSlide(targetSlide);
    setAnimatedSlide(targetSlide);

    setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_MS);
  };

  // Auto loop with 5 second delay after content loads
  useEffect(() => {
    if (realFaceCount === 0 || isTransitioning) return;

    const timer = setTimeout(() => {
      nextSlide();
    }, AUTO_SLIDE_DELAY);

    return () => clearTimeout(timer);
  }, [currentSlide, realFaceCount, isTransitioning]); // Run when currentSlide changes

  if (realFaceCount === 0) return null;

  // const cubeStyle = {
  //   transform: `translateZ(${-depth}px) rotateX(${currentSlide * -angleStep}deg)`,
  //   transition: isTransitioning
  //     ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
  //     : "none",
  // };
  const cubeStyle = {
  transform: `translateZ(${-depth}px) rotateX(${currentSlide * -angleStep}deg)`,
  transition: isTransitioning
    ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
    : "none",
};


  return (
    <div className="cube-slider-main-outer" ref={containerRef}>
      <div className="cube-slider-main-banner" style={cubeStyle}>
        {displayBanners.map((s, i) => {
          const faceTransform = {
            transform: `rotateX(${i * angleStep}deg) translateZ(${depth}px)`,
          };

          const isActive = i === currentSlide;
          const shouldAnimate = i === animatedSlide && isActive;

          return (

            <div
              key={`${s.id}-${i}`}
              className={`cube-slider-main-face ${isActive ? "cube-slider-main-active" : ""} ${s.banner_alignment === "right" ? "align-left" : "align-center"
                }`}
              style={faceTransform}
            >
              <div
                className="cube-slider-main-bg-image"
                style={{ backgroundImage: `url(${s.banner_image})` }}
              />
              {s?.banner_category === "custom-text" && (
                <>
                <div className="cube-slider-main-overlay" />
                
                <div className="cube-slider-main-content">
                  <h1
                    className={`cube-slider-main-title banner-title-font ${shouldAnimate ? "cube-slider-main-animate" : ""
                      }`}
                  >
                    {s.banner_title}
                  </h1>
                  <p
                    className={`cube-slider-main-sub ${shouldAnimate ? "cube-slider-main-animate" : ""
                      }`}
                  >
                    {s.banner_description}
                  </p>
                </div>
                </>
              )}
            </div>

          );
        })}
      </div>

      <button className="cube-slider-main-arrow cube-slider-main-prev" onClick={prevSlide}>‹</button>
      <button className="cube-slider-main-arrow cube-slider-main-next" onClick={nextSlide}>›</button>

      {/* <div className="cube-slider-main-dots">
        {homeBanners.map((_, i) => (
          <button
            key={i}
            className={`cube-slider-main-dot ${i === activeIndex ? "cube-slider-main-active" : ""}`}
            onClick={() => goToSlide(i)}
          />
        ))}
      </div> */}
    </div>
  );
};

export default CubeSliderMain;